import { useContext, useEffect } from 'react';
import { ActionDependant } from '@ea-lab/reactive-json';
import { ArrowLeft, ShieldAlert, Tags, UsersRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { DocFiltersContext } from '../DocFiltersContext';
import DocSettingsCategories from './DocSettingsCategories';
import DocSettingsGroups from './DocSettingsGroups';

const TABS = [
    { id: 'categories', label: 'Catégories', icon: Tags },
    { id: 'groups', label: 'Groupes', icon: UsersRound },
];

export const DocSettings = ({ props }) => {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const filtersCtx = useContext(DocFiltersContext);
    const activeTab = filtersCtx?.settingsSection ?? 'categories';
    const setActiveTab = filtersCtx?.setSettingsSection;

    useEffect(() => {
        if (!isAdmin) {
            const timeout = setTimeout(() => navigate('/docs', { replace: true }), 3000);
            return () => clearTimeout(timeout);
        }
    }, [isAdmin, navigate]);

    if (!isAdmin) {
        return (
            <ActionDependant {...props}>
                <div id="doc-settings-page" className="flex flex-col items-center justify-center py-20 text-center">
                    <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
                        <ShieldAlert size={28} className="text-amber-500" />
                    </div>
                    <h1 className="text-lg font-semibold text-stone-900 mb-1">Accès restreint</h1>
                    <p className="text-sm text-stone-500 max-w-sm">
                        Cette page est réservée aux administrateurs. Vous allez être redirigé vers la documentation.
                    </p>
                </div>
            </ActionDependant>
        );
    }

    return (
        <ActionDependant {...props}>
            <div id="doc-settings-page">
                {/* Header */}
                <div id="doc-settings-header" className="mb-8">
                    <button
                        type="button"
                        onClick={() => navigate('/docs')}
                        className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 transition-colors mb-4"
                    >
                        <ArrowLeft size={14} />
                        <span>Retour à la documentation</span>
                    </button>
                    <h1 className="text-2xl font-bold text-stone-900 tracking-tight">
                        Configuration
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">
                        Gérez les paramètres de votre espace documentaire.
                    </p>
                </div>

                {/* Tabs */}
                <div id="doc-settings-tabs" className="border-b border-stone-200 mb-8">
                    <nav className="flex gap-6" aria-label="Onglets de configuration">
                        {TABS.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveTab?.(tab.id)}
                                    className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                                        isActive
                                            ? 'border-stone-900 text-stone-900'
                                            : 'border-transparent text-stone-400 hover:text-stone-600 hover:border-stone-300'
                                    }`}
                                    aria-selected={isActive}
                                    role="tab"
                                >
                                    <Icon size={15} />
                                    <span>{tab.label}</span>
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Tab content */}
                <div id="doc-settings-content">
                    {activeTab === 'categories' && <DocSettingsCategories />}
                    {activeTab === 'groups' && <DocSettingsGroups />}
                </div>
            </div>
        </ActionDependant>
    );
};
